import { useCallback, useState, type MouseEvent, type ReactNode } from 'react';
import { trackSeeMoreClick } from '../../../utils/tracking/searchBrowse/filterTracking.ts';
import classnames from 'classnames';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';

// styles
import styles from './SbSharedRefineMenuExpandableItemList.scss';

type ObjectType = Record<string, unknown>;
type ExpandableValue<T> = T extends null ? null : T & { isHidden: boolean };

type Props<T extends ObjectType | null> = {
    availableFilterValues?: ReadonlyArray<T> | null;
    collapsedItemCount?: number;
    filterName: string;
    children: (props: { valuesToShow: ExpandableValue<T>[] }) => ReactNode;
};

export const SbSharedRefineMenuExpandableItemList = <T extends ObjectType | null>({
    availableFilterValues,
    collapsedItemCount = 5,
    filterName,
    children,
}: Props<T>): ReactNode => {
    const [isExpanded, setIsExpanded] = useState(false);

    const canSeeMore = (availableFilterValues?.length || 0) > collapsedItemCount;
    const valuesToShow: Array<ExpandableValue<T>> = (availableFilterValues || []).map(
        (filter, index) => {
            if (filter === null) {
                return null as ExpandableValue<T>;
            }
            return {
                ...filter,
                isHidden: isExpanded ? false : collapsedItemCount <= index,
            } as ExpandableValue<T>;
        }
    );

    const onClick = useCallback(
        (e: MouseEvent) => {
            e.preventDefault();
            if (!isExpanded) {
                trackSeeMoreClick(filterName);
            }
            setIsExpanded(current => !current);
        },
        [isExpanded, filterName]
    );

    return (
        <>
            {children({ valuesToShow })}
            {canSeeMore && (
                <Link
                    buyerLinkType="standardLink"
                    className={classnames(styles.button, {
                        [styles.buttonSpacingTop]: !isExpanded && collapsedItemCount !== 0,
                    })}
                    onClick={onClick}
                    ariaExpanded={isExpanded}
                    dataTn={`see-${isExpanded ? 'less' : 'more'}`}
                >
                    <FormattedMessage
                        id="sb.SbSharedRefineMenu.typeahead.seeMore"
                        defaultMessage={`{isExpanded, select,
                                true {See Less}
                                other {See More}
                            }`}
                        values={{ isExpanded }}
                    />
                </Link>
            )}
        </>
    );
};
